// ./src/components/features/Notes/NotesList/NotesList.tsx
//
// Event Notes list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row, selection-ready. A private
// note is badged with a lock. Mirrors the Points of Interest list conventions
// (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Avatar,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { Note, NOTE_MESSAGE_TYPE, Tag } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalNotesContext } from "@/contexts/NotesContext";
import { getMostContrastingColor } from "@/utils/colorHelpers";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonLines, CardSkeletonCheckbox, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { useEntranceAnimation, entranceListVariants, entranceCardVariants } from "@/hooks/useEntranceAnimation";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

dayjs.extend(relativeTime);

// ============================================
// Props
// ============================================

export interface NotesListProps {
  data?: Note[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditNote?: (note: Note) => void;
  onDeleteNote?: (note: Note) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddNote?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const MAX_VISIBLE_TAGS = 3;
const PREVIEW_LENGTH = 140;

/** Collapse a note body (HTML for rich text, markdown source otherwise) into a plain-text preview. */
const notePreview = (note: Note): string => {
  const raw = note.message || "";

  if (!raw) {
    return "";
  }

  let text = raw;

  if (note.messageType !== NOTE_MESSAGE_TYPE.MARKDOWN) {
    // Rich text is HTML — strip tags.
    text = text.replace(/<[^>]*>/g, " ");
  } else {
    // Markdown — drop the most common syntax noise.
    text = text
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/[#>*_`~[\]()!-]/g, " ");
  }

  text = text.replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

  return text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text;
};

const TagPill: React.FC<{ tag: Tag }> = ({ tag }) => {
  const color = tag.color || "#6B7280";

  return (
    <Badge
      size="sm"
      radius="sm"
      styles={{ root: { backgroundColor: color, color: getMostContrastingColor(color), textTransform: "none" } }}
    >
      {tag.name}
    </Badge>
  );
};

const TagCell: React.FC<{ note: Note }> = ({ note }) => {
  const tags = note.tags || [];

  if (tags.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const extra = tags.length - visible.length;

  return (
    <Group gap={4} wrap="wrap">
      {visible.map((tag) => (
        <TagPill key={tag.id} tag={tag} />
      ))}
      {extra > 0 && (
        <Badge size="sm" radius="sm" variant="light" color="gray">
          +{extra}
        </Badge>
      )}
    </Group>
  );
};

const NoteBadges: React.FC<{ note: Note }> = ({ note }) => {
  const { t } = useTranslation(["events"]);

  if (!note.isPrivate) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="grape" leftSection={<AppIcon iconName="Lock" size={10} />}>
      {t("events:notes.private", { defaultValue: "Private" })}
    </Badge>
  );
};

const NoteIcon: React.FC<{ note: Note; size?: number }> = ({ note, size = 34 }) => (
  <ThemeIcon variant="light" color={note.isPrivate ? "grape" : "blue"} radius="xl" size={size}>
    <AppIcon iconName={note.isPrivate ? "Lock" : "Notes"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

/** The creator's display name, or null when it couldn't be resolved. */
const authorName = (note: Note): string | null => {
  const u = note.createdByUser;

  if (!u) {
    return null;
  }

  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();

  return full || u.username || null;
};

/** "Added by {name} · {relative}" footer with the absolute date on hover. Always present, so a
 *  title-only note still has a second line (which keeps the icon vertically balanced with the text). */
const NoteMeta: React.FC<{ note: Note }> = ({ note }) => {
  const { t } = useTranslation(["events"]);

  const name = authorName(note);
  const created = note.createdAt ? dayjs(note.createdAt) : null;
  const hasDate = !!created && created.isValid();
  const relative = hasDate ? created!.fromNow() : null;
  const absolute = hasDate ? created!.format("MMM D, YYYY · h:mm A") : null;

  const label = name
    ? t("events:notes.addedByOn", { defaultValue: "Added by {{name}}", name })
    : t("events:notes.addedOn", { defaultValue: "Added" });

  const text = relative ? `${label} · ${relative}` : label;

  return (
    <Tooltip label={absolute ?? ""} withArrow position="bottom" disabled={!absolute} openDelay={300}>
      <Group gap={6} wrap="nowrap" align="center" mt={4}>
        <Avatar src={note.createdByUser?.avatarUrl || undefined} size={16} radius="xl">
          {(name ?? "?").slice(0, 1).toUpperCase()}
        </Avatar>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {text}
        </Text>
      </Group>
    </Tooltip>
  );
};

const RowActions: React.FC<{
  note: Note;
  onEdit?: (n: Note) => void;
  onDelete?: (n: Note) => void;
  isDeleting: boolean;
}> = ({ note, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(note, "edit");
  const showDelete = !!onDelete && recordHasRight(note, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(note)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(note)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  note: Note;
  title: string;
  preview: string;
  onEdit?: (n: Note) => void;
  onDelete?: (n: Note) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const NoteTableRow: React.FC<RowProps> = ({
  note,
  title,
  preview,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => note.id && onToggleSelect?.(note.id)}
              disabled={isDeleting}
              aria-label={title}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <NoteIcon note={note} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {title}
              </Text>
              <NoteBadges note={note} />
            </Group>
            {preview && (
              <Text size="sm" c="dimmed" lineClamp={1} mt={2}>
                {preview}
              </Text>
            )}
            <NoteMeta note={note} />
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 260 }}>
        <TagCell note={note} />
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions note={note} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const NoteTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Box style={{ flex: 1 }}>
          <Skeleton height={14} width="40%" maw={200} mb={6} />
          <Skeleton height={12} width="70%" maw={360} />
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <Skeleton height={18} width={140} radius="sm" />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const NoteCardComponent: React.FC<CardProps> = ({
  note,
  title,
  preview,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => note.id && onToggleSelect?.(note.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(note, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(note),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(note, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(note),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, note, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            mt={2}
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={title}
          />
        )}
        <NoteIcon note={note} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {title}
            </Text>
            <NoteBadges note={note} />
          </Group>
          {preview && (
            <Text size="sm" c="dimmed" lineClamp={2} mt={2}>
              {preview}
            </Text>
          )}
          {(note.tags?.length ?? 0) > 0 && (
            <Box mt={6}>
              <TagCell note={note} />
            </Box>
          )}
          <NoteMeta note={note} />
        </Box>
        {showActions && <RowActions note={note} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const NoteCard = React.memo(NoteCardComponent);

const NoteCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={4} sizes={["md", "sm", "sm", "xs"]} gap={2} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const NotesListComponent: React.FC<NotesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditNote,
  onDeleteNote,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddNote,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditNote, onDeleteNote);

  const notesContext = useOptionalNotesContext();

  const notes = dataProp ?? notesContext?.notes ?? [];
  const loading = loadingProp ?? notesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? notesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? notesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? notesContext?.loadMore;
  const isRemoving = isRemovingProp ?? notesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: notes.length });

  const handleEdit = useCallback(
    (note: Note) => {
      closeAllCards();
      onEditNote?.(note);
    },
    [onEditNote, closeAllCards]
  );

  const handleDelete = useCallback(
    (note: Note) => {
      closeAllCards();
      onDeleteNote?.(note);
    },
    [onDeleteNote, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = notesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && notes.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("events:notes.emptyState.noMatch", { defaultValue: "No notes match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("events:notes.emptyState.title", { defaultValue: "No notes yet" })}
          description={t("events:notes.emptyState.description", {
            defaultValue: "Jot down reminders, decisions and details for this event. Keep a note private, or share it with your team.",
          })}
          iconName="Notes"
          actionLabel={t("events:notes.emptyState.actionLabel", { defaultValue: "Add note" })}
          onAction={onAddNote}
          size="md"
        />
      </Paper>
    );
  }

  const noteTitle = (note: Note): string =>
    (note.title || "").trim() || t("events:notes.untitled", { defaultValue: "Untitled note" });

  const rowIsSelectable = (note: Note): boolean =>
    isSelectable && ((!!onEditNote && recordHasRight(note, "edit")) || (!!onDeleteNote && recordHasRight(note, "remove")));

  const rowProps = (note: Note): RowProps => ({
    note,
    title: noteTitle(note),
    preview: notePreview(note),
    onEdit: onEditNote ? handleEdit : undefined,
    onDelete: onDeleteNote ? handleDelete : undefined,
    highlighted: note.id === highlightedId,
    isDeleting: isRemoving(note.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(note),
    selected: selection?.isSelected(note.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = notes
    .filter((n) => rowIsSelectable(n))
    .map((n) => n.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("events:notes.list.columns.note", { defaultValue: "Note" })}</Table.Th>
                <Table.Th style={{ width: 260 }}>
                  {t("events:notes.list.columns.tags", { defaultValue: "Tags" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <NoteTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : notes.map((note) => <NoteTableRow key={note.id} {...rowProps(note)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <NoteCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {notes.map((note) => (
            <motion.div
              key={note.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <NoteCard
                {...rowProps(note)}
                forceClose={shouldForceClose(note.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(note.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(note.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const NotesList = React.memo(NotesListComponent);
